import React, { useState, useEffect } from 'react';
import { Shield, TrendingUp, TrendingDown, Minus, AlertTriangle } from 'lucide-react';

const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'http://fleet.host/api'
  : '/api';

interface SafetyGrades {
  safety_grade: number;
  fuel_grade: number;
  safety_trend?: number;
}

interface EventGroup {
  id: number;
  name: string;
  count: number;
}

interface DriverSafety {
  metadata: {
    driver_code?: number;
    driver_name?: string;
    driver_id?: number;
    license_plates?: string[];
    license_plate?: string;  // For vehicle mode
  };
  grades: SafetyGrades;
  event_groups: EventGroup[];
}

interface DriverSafetyData {
  metadata: {
    pagination: {
      page_number: number;
      page_size: number;
      total_count: number;
      page_count: number;
    };
  };
  safety: DriverSafety[];
}

interface Props {
  fromDate?: string;
  toDate?: string;
  onClose: () => void;
}

const SafetyDashboard: React.FC<Props> = ({ fromDate, toDate, onClose }) => {
  const [data, setData] = useState<DriverSafetyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'drivers' | 'vehicles'>('drivers');
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadSafetyData();
  }, [viewMode, page, fromDate, toDate]);

  const loadSafetyData = async () => {
    setLoading(true);
    setError(null);

    const endpoint = viewMode === 'drivers' 
      ? `${API_BASE_URL}/ituran-safety-drivers.php`
      : `${API_BASE_URL}/ituran-safety-vehicles.php`;

    const params = new URLSearchParams({
      page: page.toString(),
      page_size: '10',
      ...(fromDate && { from_date: fromDate }),
      ...(toDate && { to_date: toDate })
    });

    try {
      const response = await fetch(`${endpoint}?${params}`);
      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error || 'Failed to load data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend?: number) => {
    if (!trend) return <Minus className="w-4 h-4 text-gray-400" />;
    if (trend > 0) return <TrendingUp className="w-4 h-4 text-green-500" />;
    return <TrendingDown className="w-4 h-4 text-red-500" />;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold">Safety Dashboard</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('drivers')}
              className={`px-4 py-2 rounded ${
                viewMode === 'drivers' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              By Drivers
            </button>
            <button
              onClick={() => setViewMode('vehicles')}
              className={`px-4 py-2 rounded ${
                viewMode === 'vehicles' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              By Vehicles
            </button>
            <button
              onClick={onClose}
              className="ml-4 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-4 flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && data && (
            <>
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          {viewMode === 'drivers' ? 'Driver' : 'Vehicle'}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Vehicle(s)
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Safety
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Fuel
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Events
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.safety.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">
                              {viewMode === 'drivers' 
                                ? (item.metadata.driver_name || `Driver #${item.metadata.driver_code === -1 ? 'Unknown' : item.metadata.driver_code}`)
                                : (item.metadata.license_plate || 'Unknown Vehicle')
                              }
                            </div>
                            {viewMode === 'drivers' && item.metadata.driver_code && item.metadata.driver_code !== -1 && (
                              <div className="text-xs text-gray-500">Code: {item.metadata.driver_code}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-700">
                              {viewMode === 'drivers' && item.metadata.license_plates ? (
                                item.metadata.license_plates.length > 1 ? (
                                  <span>{item.metadata.license_plates.length} vehicles</span>
                                ) : (
                                  <span>{item.metadata.license_plates[0]}</span>
                                )
                              ) : (
                                <span>{item.metadata.license_plate || '-'}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${
                                item.grades.safety_grade >= 95 ? 'bg-green-100 text-green-800' :
                                item.grades.safety_grade >= 85 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {item.grades.safety_grade}
                              </span>
                              <span className="text-sm">{getTrendIcon(item.grades.safety_trend)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-center">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${
                                item.grades.fuel_grade >= 95 ? 'bg-green-100 text-green-800' :
                                item.grades.fuel_grade >= 85 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {item.grades.fuel_grade}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {item.event_groups && item.event_groups.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {item.event_groups.map((event) => (
                                  <span
                                    key={event.id}
                                    className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                                  >
                                    <span className="capitalize font-medium">{event.name}:</span>
                                    <span className="font-bold">{event.count}</span>
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400 italic">No events</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {data.metadata.pagination.page_count > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2">
                    Page {page} of {data.metadata.pagination.page_count}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(data.metadata.pagination.page_count, p + 1))}
                    disabled={page >= data.metadata.pagination.page_count}
                    className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SafetyDashboard;
